import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import {
  GET_TASK_TEMPLATES,
  TASK_TEMPLATE_DELETE,
} from '@atom/graph/taskTemplate';
import { Icon, IconButton, Modal, Snackbar, TextField } from '@atom/mui';
import { CloseIcon, SearchIcon } from '@atom/mui/icons';
import fonts from '@atom/styles/fonts';
import { TaskCreateInput, TaskUpdateInput } from '@atom/types/task';
import {
  TaskTemplate,
  TaskTemplatesConnection,
} from '@atom/types/taskTemplate';
import { getErrorCode } from '@atom/utilities/validationUtilities';

import EmptySearchTemplate from './EmptySearchTemplate';
import EmptyTaskTemplate from './EmptyTaskTemplate';
import TaskTemplateContent from './TaskTemplateContent';

import classes from './taskCreate.module.css';

const LIMIT = 25;
const SEARCH_THRESHOLD = 3;
const DEBOUNCE_TIME = 500;
const HTTP_STATUS_CONFLICT = 409;

export interface Props {
  workOrderId: string;
  open: boolean;
  onClose: () => void;
  handleConfirm: (
    property: keyof TaskCreateInput | keyof TaskUpdateInput,
    value: string,
  ) => void;
  confirmButtonText?: string;
  disableDelete?: boolean;
  title?: string;
}

const styles = {
  modal: {
    padding: '2rem',
  },
  input: {
    '&.MuiInput-root': {
      fontSize: fonts.md,
      height: '3rem',
      paddingLeft: '1rem',
    },
    '& .MuiInput-input': {
      paddingLeft: '1rem',
    },
  },
};

const TaskCreateTemplateModal = ({
  workOrderId,
  open,
  onClose,
  handleConfirm,
  confirmButtonText = 'Create',
  disableDelete,
  title = 'Create Task',
}: Props) => {
  const ref = useRef(null);

  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [total, setTotal] = useState<number>(0);
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  const [searchTaskTemplates, setSearchTaskTemplates] = useState<
    TaskTemplate[]
  >([]);
  const [selectedTaskTemplate, setSelectedTaskTemplate] =
    useState<TaskTemplate>();

  const searching: boolean = useMemo(
    () => search.length >= SEARCH_THRESHOLD,
    [search],
  );

  const [getTaskTemplates, { loading: taskTemplatesLoading }] = useLazyQuery(
    GET_TASK_TEMPLATES,
    {
      fetchPolicy: 'network-only',
    },
  );

  const applyTaskTemplatesResult = (
    data: { taskTemplates: TaskTemplatesConnection },
    isSearching: boolean,
  ) => {
    const nextTaskTemplates = R.pathOr(
      [],
      ['taskTemplates', 'taskTemplates'],
      data,
    );

    if (isSearching) {
      setSearchTaskTemplates(nextTaskTemplates);
    } else {
      setTaskTemplates(prev => [...prev, ...nextTaskTemplates]);
      setTotal(data?.taskTemplates?.totalCount || 0);
    }
  };

  const [taskTemplateDelete] = useMutation(TASK_TEMPLATE_DELETE);

  const resetState = () => {
    setTaskTemplates([]);
    setPage(1);
    setTotal(0);
    setSearch('');
    setSearchTaskTemplates([]);
    setSelectedTaskTemplate(null);
  };

  const handleGetTaskTemplates = async () => {
    const { data } = await getTaskTemplates({
      variables: {
        input: {
          workOrderId,
          sortBy: 'name',
          isAscending: true,
          limit: LIMIT,
          page,
        },
      },
    });

    applyTaskTemplatesResult(data, false);
  };

  const handleDelete = async (id: string) => {
    try {
      await taskTemplateDelete({ variables: { id } });

      resetState();
      handleGetTaskTemplates();
    } catch (err) {
      const message =
        getErrorCode(err) === HTTP_STATUS_CONFLICT
          ? 'The task template cannot be deleted because it was used to create a task'
          : 'An unknown error occurred';

      Snackbar.error({ message });

      resetState();
      handleGetTaskTemplates();
    }
  };

  useEffect(() => {
    if (open) {
      handleGetTaskTemplates();
    } else {
      resetState();
    }
  }, [open]);

  const clearSearch = async () => {
    resetState();

    const { data } = await getTaskTemplates({
      variables: {
        input: {
          workOrderId,
          sortBy: 'name',
          isAscending: true,
          limit: LIMIT,
          page: 1,
        },
      },
    });

    applyTaskTemplatesResult(data, false);
  };

  const debouncedSearch = useCallback(
    debounce(async (name: string) => {
      const { data } = await getTaskTemplates({
        variables: {
          input: {
            workOrderId,
            sortBy: 'name',
            isAscending: true,
            limit: LIMIT,
            page,
            name,
          },
        },
      });

      applyTaskTemplatesResult(data, true);
    }, DEBOUNCE_TIME),
    [],
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);

    if (!value) {
      debouncedSearch.cancel();
    } else if (value.length >= SEARCH_THRESHOLD) {
      debouncedSearch(value);
    } else {
      debouncedSearch.cancel();
    }
  };

  const handlePageScroll = async (nextPage: number) => {
    setPage(nextPage);

    const { data } = await getTaskTemplates({
      variables: {
        input: {
          workOrderId,
          sortBy: 'name',
          isAscending: true,
          limit: LIMIT,
          page: nextPage,
        },
      },
    });

    applyTaskTemplatesResult(data, false);
  };

  const handleSubmit = () => {
    if (selectedTaskTemplate) {
      handleConfirm('templateId', selectedTaskTemplate?.id);
      onClose();
    }
  };

  const taskTemplatesData =
    searching && !R.isNil(searchTaskTemplates)
      ? searchTaskTemplates
      : taskTemplates;

  const showEmptyResults = searching && R.isEmpty(searchTaskTemplates);

  return (
    <Modal
      title={title}
      open={open}
      onConfirm={handleSubmit}
      onCancel={onClose}
      onExited={resetState}
      confirmButtonText={confirmButtonText}
      contentStyle={styles.modal}
      disabled={!selectedTaskTemplate}
      data-cy="taskCreateTemplateModal"
    >
      <div>
        <div className={classes.searchContent}>
          <div className={classes.searchBox}>
            <TextField
              slotProps={{
                input: {
                  startAdornment: <Icon icon={SearchIcon} />,
                  endAdornment: (
                    <IconButton
                      onClick={clearSearch}
                      data-cy="taskCreateTemplateSearchClearButton"
                    >
                      <Icon icon={CloseIcon} />
                    </IconButton>
                  ),
                  sx: styles.input,
                },
              }}
              placeholder="Search Task Templates"
              name="search"
              value={search}
              onChange={event => handleSearch(event.target.value)}
              data-cy="taskCreateTemplateSearchInput"
            />
          </div>
        </div>
        <div className={classes.contentContainer} ref={ref}>
          {showEmptyResults ? (
            <EmptySearchTemplate taskTemplatesLoading={taskTemplatesLoading} />
          ) : !total ? (
            <EmptyTaskTemplate />
          ) : (
            <TaskTemplateContent
              outerRef={ref}
              taskTemplates={taskTemplatesData}
              taskTemplatesLoading={taskTemplatesLoading}
              total={total}
              searching={searching}
              page={page}
              selectedTaskTemplate={selectedTaskTemplate}
              setSelectedTaskTemplate={setSelectedTaskTemplate}
              handleDelete={handleDelete}
              handlePageScroll={handlePageScroll}
              disableDelete={disableDelete}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TaskCreateTemplateModal;
